import { useEffect, useState } from 'react';

/**
 * `useState` mirror that writes to localStorage on change and reads on mount.
 * Falls back to `initial` when the key is unset or unparseable. SSR-safe.
 */
export function usePersistedState<T>(key: string, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // localStorage unavailable: silent fall-through.
    }
  }, [key, value]);

  // Accepts an updater as well as a value, so callers holding a record (the
  // inbox's per-conversation drafts, say) can merge without reading the
  // current value into their own closure first.
  return [value, setValue];
}
